import { useCallback } from "react";
import {
  ApplicationFlowState,
  IdVerificationQuestion as IdVerificationQuestionType,
} from "src/apps/AppFlow/screens/root/Layout";
import SingleSelectQuestion from "src/apps/AppFlow/screens/root/Layout/sections/IdentityVerification/pages/SingleSelectQuestion";
import { useIdentityVerificationQuestionsWithContext } from "src/apps/AppFlow/screens/root/Layout/sections/IdentityVerification/query";
import { ReducerReturn } from "src/hooks/useReducerPageState";

import { ValueError } from "@fraction/shared";

export interface IdVerificationQuestionProps extends ReducerReturn<ApplicationFlowState> {
  question: IdVerificationQuestionType;
  questionIndex: number;
  onNext?: () => void;
  onBack?: () => void;
}

const setAnswerToQuestions = (
  questions: IdVerificationQuestionType[],
  answerNumber: string | undefined,
  questionIndex: number
) => [
  ...questions.slice(0, questionIndex),
  { ...questions[questionIndex], answerNumber: Number(answerNumber) },
  ...questions.slice(questionIndex + 1),
];

const IdVerificationQuestion = ({
  state,
  dispatch,
  extract,
  question,
  questionIndex,
  onNext,
  onBack,
  ...props
}: IdVerificationQuestionProps) => {
  const { onChange } = extract("idVerificationQuestions");
  const idVerification = useIdentityVerificationQuestionsWithContext({ state, dispatch });
  const { questions, answerQuestions, loading, attemptId, creditBureau } = idVerification;
  const primaryApplicant = state.applicants?.[0];

  const handleSetPageAnswer = useCallback(
    async (val: string | undefined) => {
      onChange?.({
        ...idVerification,
        questions: setAnswerToQuestions(questions, val, questionIndex),
      });
    },
    [onChange, questions, idVerification, questionIndex]
  );

  const handleOnNext = useCallback(async () => {
    // only need to submit once we are at the last question page
    onNext?.();
  }, [onNext]);

  const handleFinish = useCallback(
    async (val: string | undefined) => {
      const answeredQuestions = setAnswerToQuestions(questions, val, questionIndex);

      if (!primaryApplicant?.id) {
        throw new ValueError("Missing primary applicant ID at ID Verification stage");
      }
      if (!attemptId) {
        throw new ValueError("Missing attemptID at ID Verification stage");
      }

      const answers = answeredQuestions.map((q) => ({
        questionNumber: q.questionNumber,
        chosenAnswerNumber: q.answerNumber,
      }));

      await answerQuestions({
        applicantId: primaryApplicant?.id,
        answers,
        attemptId,
      });

      onNext?.();
    },
    [primaryApplicant?.id, answerQuestions, attemptId, questions, questionIndex, onNext]
  );

  const handleBack = useCallback(() => {
    onBack?.();
  }, [onBack]);

  return (
    <SingleSelectQuestion
      {...props}
      nextLoading={loading}
      value={question?.answerNumber?.toString()}
      onChange={handleSetPageAnswer}
      onNext={questionIndex !== questions.length - 1 ? handleOnNext : handleFinish}
      title={question.questionText}
      options={question.possibleAnswers?.map((answer) => ({
        label: answer.answerText,
        value: answer.answerNumber?.toString(),
      }))}
      creditBureau={creditBureau}
      onBack={handleBack}
    />
  );
};

export default IdVerificationQuestion;
